import type {
  UserStatus,
  PaymentStatus,
  DocumentStatus,
  ListingStatus,
  CompanyCreditStatus,
  CreditApplicationStatus,
  QuoteClaimStatus,
} from "../schemas/validators/enums.lib";
import type { PayableStatus } from "../schemas/validators/financial.lib";

export type {
  OrderStage,
  OrderPaymentStatus,
  UserStatus,
  PaymentStatus,
  DocumentStatus,
  ListingStatus,
  CompanyCreditStatus,
  CreditApplicationStatus,
  QuoteClaimStatus,
  SupportRequestStatus,
  SubscriptionStatus,
} from "../schemas/validators/enums.lib";
import { literalValues } from "@shared/validation/literal-values";

export * from "./orderStages.lib";

export const USER_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  INVITED: "invited",
  SUSPENDED: "suspended",
  PENDING_CLAIM: "pending_claim",
} as const satisfies Record<string, UserStatus>;

export const USER_STATUSES = literalValues(USER_STATUS);

export const PAYMENT_STATUS = {
  PENDING: "pending",
  AUTHORIZED: "authorized",
  PAID: "paid",
  REFUNDED: "refunded",
  FAILED: "failed",
} as const satisfies Record<string, PaymentStatus>;

export const PAYMENT_STATUSES = literalValues(PAYMENT_STATUS);

export const DOCUMENT_STATUS = {
  GENERATING: "generating",
  GENERATED: "generated",
  UPLOADED: "uploaded",
  SENT: "sent",
  SYNCED: "synced",
  VIEWED: "viewed",
  VOIDED: "voided",
  FAILED: "failed",
} as const satisfies Record<string, DocumentStatus>;

export const DISPUTE_STATUS = {
  OPEN: "open",
  WARNING_NEEDS_RESPONSE: "warning_needs_response",
  WARNING_UNDER_REVIEW: "warning_under_review",
  WARNING_CLOSED: "warning_closed",
  NEEDS_RESPONSE: "needs_response",
  UNDER_REVIEW: "under_review",
  WON: "won",
  LOST: "lost",
  PREVENTED: "prevented",
} as const;

export type DisputeStatus =
  (typeof DISPUTE_STATUS)[keyof typeof DISPUTE_STATUS];

export const DISPUTE_STATUSES = literalValues(DISPUTE_STATUS);

export const ACTIVE_DISPUTE_STATUSES: readonly DisputeStatus[] = [
  DISPUTE_STATUS.OPEN,
  DISPUTE_STATUS.WARNING_NEEDS_RESPONSE,
  DISPUTE_STATUS.WARNING_UNDER_REVIEW,
  DISPUTE_STATUS.NEEDS_RESPONSE,
  DISPUTE_STATUS.UNDER_REVIEW,
] as const;

export const CLOSED_DISPUTE_STATUSES: readonly DisputeStatus[] = [
  DISPUTE_STATUS.WARNING_CLOSED,
  DISPUTE_STATUS.WON,
  DISPUTE_STATUS.LOST,
  DISPUTE_STATUS.PREVENTED,
] as const;

export const CLOSED_WITHOUT_FUNDS_DISPUTE_STATUSES: readonly DisputeStatus[] = [
  DISPUTE_STATUS.WARNING_CLOSED,
  DISPUTE_STATUS.PREVENTED,
] as const;

export const PAYOUT_STATUS = {
  PAID: "paid",
  PENDING: "pending",
  IN_TRANSIT: "in_transit",
  CANCELED: "canceled",
  FAILED: "failed",
} as const;

export type PayoutStatus = (typeof PAYOUT_STATUS)[keyof typeof PAYOUT_STATUS];

export const ACCOUNT_CLAIM_STATUS = {
  PENDING: "pending",
  CLAIMED: "claimed",
  EXPIRED: "expired",
} as const;

export const QUOTE_CLAIM_STATUS = {
  PENDING: "pending",
  CLAIMED: "claimed",
  EXPIRED: "expired",
  REVOKED: "revoked",
} as const satisfies Record<string, QuoteClaimStatus>;

export const CREDIT_APPLICATION_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  DENIED: "denied",
  EXPIRED: "expired",
} as const satisfies Record<string, CreditApplicationStatus>;

export const COMPANY_CREDIT_STATUS = {
  NONE: "none",
  PENDING: "pending",
  APPROVED: "approved",
  SUSPENDED: "suspended",
} as const satisfies Record<string, CompanyCreditStatus>;

export const TERMINAL_INVOICE_STATUSES: ReadonlySet<string> = new Set([
  "paid",
  "void",
  "written_off",
  "uncollectible",
  "deleted",
  "missing",
]);

export const UNFINALIZED_INVOICE_STATUSES: ReadonlySet<string> = new Set([
  "draft",
  "deleted",
  "missing",
]);

export const TERMINAL_PAYABLE_STATUSES: ReadonlySet<string> = new Set([
  "paid",
  "void",
]);

export const BILL_PUSH_PAYABLE_STATUSES: ReadonlySet<PayableStatus> = new Set<PayableStatus>([
  "approved",
  "scheduled",
  "partially_paid",
  "paid",
]);

export const VENDOR_DUE_DATE_FOLLOWS_PICKUP_STATUSES: ReadonlySet<PayableStatus> = new Set([
  "expected",
  "received",
  "under_review",
  "approved",
]);

export const IN_REVIEW_PAYABLE_STATUSES: ReadonlySet<PayableStatus> = new Set<PayableStatus>([
  "received",
  "under_review",
]);

export const OPEN_INVOICE_STATUSES = [
  "open",
  "overdue",
  "partially_paid",
] as const;

export const OPEN_PAYABLE_STATUSES = [
  "expected",
  "received",
  "under_review",
  "approved",
  "scheduled",
  "partially_paid",
  "disputed",
] as const satisfies ReadonlyArray<PayableStatus>;

export const TERMINAL_SETTLEMENT_STATUSES: ReadonlySet<string> = new Set([
  "settled",
  "reversed",
  "canceled",
  "failed",
]);

export const SATISFIED_DOC_STATUSES: ReadonlySet<string> = new Set([
  "accepted",
  "waived",
  "not_applicable",
]);

export const LISTING_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  PUBLISHED: "published",
  REJECTED: "rejected",
  ARCHIVED: "archived",
} as const satisfies Record<string, ListingStatus>;

export const LISTING_STATUSES = literalValues(LISTING_STATUS);
